import { useCallback, useMemo } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';

import { DeleteForeverOutlinedIcon } from '@occc/ui-design-system';
import { KioskAdScheduleInput, Weekday } from '@occc/graphql';

import { WeekdaySelector } from './weekday-selector.component';
import { ScheduleTimeRange } from './schedule-time-range.component';

export type ScheduleBlockErrors = {
  endTime?: string;
  days?: string;
};

type ScheduleBlockProps = {
  index: number;
  value: KioskAdScheduleInput;
  showAddButton: boolean;
  canDelete: boolean;
  isHeaderVisible?: boolean;
  isBorderVisible?: boolean;
  onAdd: () => void;
  onChange: (value: KioskAdScheduleInput) => void;
  onRemove: () => void;
  errors?: ScheduleBlockErrors;
};

export const ScheduleBlock = ({
  index,
  value,
  onChange,
  onRemove,
  showAddButton,
  onAdd,
  canDelete,
  isHeaderVisible = false,
  isBorderVisible = false,
  errors,
}: ScheduleBlockProps) => {
  const theme = useTheme();

  const handleDayToggle = useCallback(
    (day: Weekday) => {
      const currentDays = value.days || [];
      const newDays = currentDays.includes(day)
        ? currentDays.filter((d) => d !== day)
        : [...currentDays, day];
      onChange({ ...value, days: newDays });
    },
    [value, onChange],
  );

  const createTimeChangeHandler = useCallback(
    (field: 'startTime' | 'endTime') => (newValue: string) => {
      onChange({ ...value, [field]: newValue });
    },
    [value, onChange],
  );

  const handleStartTimeChange = useMemo(
    () => createTimeChangeHandler('startTime'),
    [createTimeChangeHandler],
  );
  const handleEndTimeChange = useMemo(
    () => createTimeChangeHandler('endTime'),
    [createTimeChangeHandler],
  );

  const borderStyle = isBorderVisible
    ? {
        borderBottom: '1px solid',
        borderColor: 'divider',
        mb: 2,
      }
    : {};

  return (
    <Box sx={borderStyle}>
      {isHeaderVisible && (
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' alignItems='center' spacing={1}>
            <MenuIcon sx={{ fontSize: 24, color: 'text.main' }} />
            <Typography variant='subtitle2' fontWeight={500}>
              Schedule {index + 1}
            </Typography>
          </Stack>

          {canDelete && (
            <IconButton
              onClick={onRemove}
              size='small'
              aria-label='Remove schedule'
              sx={{
                color: 'error.main',
                backgroundColor: lighten(theme.palette.error.main, 0.9),
                '&:hover': {
                  backgroundColor: lighten(theme.palette.error.main, 0.7),
                },
                borderRadius: 2,
                p: 0.5,
              }}
            >
              <DeleteForeverOutlinedIcon color='inherit' />
            </IconButton>
          )}
        </Stack>
      )}

      <WeekdaySelector
        error={errors?.days}
        selectedDays={value.days || []}
        onDayToggle={handleDayToggle}
      />

      <Box mb={3}>
        <ScheduleTimeRange
          startTime={value.startTime}
          endTime={value.endTime}
          onStartTimeChange={handleStartTimeChange}
          onEndTimeChange={handleEndTimeChange}
          showAddButton={showAddButton}
          onAdd={onAdd}
        />
        {errors?.endTime && (
          <Typography variant='caption' color='error' sx={{ mt: 1, display: 'block' }}>
            {errors.endTime}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
